import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, DollarSign, Activity, X } from 'lucide-react';
import PriceBoxplot from './PriceBoxplot';
import StockChart from './StockChart';
import { carService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const StatsPanel = ({ selectedQuery, yearRange, isVisible, onToggle }) => {
  const { userProfile } = useAuth();
  const [stats, setStats] = useState(null);
  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (selectedQuery && isVisible) {
      loadStats();
    }
  }, [selectedQuery, yearRange, isVisible]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Construir parámetros de consulta con rango de años
      const params = new URLSearchParams({ query: selectedQuery });
      if (yearRange && yearRange[0] && yearRange[1]) {
        params.append('min_year', yearRange[0]);
        params.append('max_year', yearRange[1]);
      }
      
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      
      // Cargar estadísticas de precios
      const priceResponse = await fetch(`${apiUrl}/stats/price-range?${params.toString()}`);
      const priceData = await priceResponse.json();
      
      if (!priceResponse.ok) {
        setError(priceData.error || 'Error al cargar estadísticas');
        return;
      }
      
      setStats(priceData);
      
      // Cargar datos de stock (no bloquea si falla)
      try {
        const stockResponse = await fetch(`${apiUrl}/stats/stock-history?${params.toString()}`);
        if (stockResponse.ok) {
          const stockDataResult = await stockResponse.json();
          setStockData(stockDataResult);
        } else {
          setStockData(null);
        }
      } catch {
        setStockData(null);
      }
    } catch (err) {
      setError('Error de conexión');
      console.error('Error loading stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="mb-8 animate-slide-up">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <BarChart3 className="w-6 h-6 text-[#7b8fa8]" />
          <h2 className="text-xl font-bold text-white">Estadísticas de Mercado</h2>
          <span className="text-sm text-gray-400 uppercase">{selectedQuery?.replace('-', ' ')}</span>
        </div>
        <button
          onClick={onToggle}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {loading && (
        <div className="glass-effect rounded-xl p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-dark-text border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Cargando estadísticas...</p>
        </div>
      )}

      {error && (
        <div className="glass-effect rounded-xl p-6 text-center">
          <Activity className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-2">Error al cargar estadísticas</p>
          <p className="text-gray-400 text-sm">{error}</p>
          <button
            onClick={loadStats}
            className="mt-4 px-4 py-2 bg-dark-text text-white rounded-lg hover:bg-dark-text/80 transition-colors"
          >
            Reintentar
          </button>
        </div>
      )}

      {stats && !loading && !error && (
        <div className="space-y-6">
          {/* Primera fila: Boxplot y KPIs */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Boxplot principal */}
            <div className="lg:col-span-2">
              <PriceBoxplot stats={stats} userAgencyId={userProfile?.agency_id} />
            </div>

            {/* KPIs adicionales */}
            <div className="space-y-4">
              {/* Total de vehículos */}
              <div className="glass-effect rounded-xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <BarChart3 className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Total Vehículos</p>
                    <p className="text-2xl font-bold text-white">{stats.count.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Precio promedio */}
              <div className="glass-effect rounded-xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-500/20 rounded-lg">
                    <DollarSign className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Precio Promedio</p>
                    <p className="text-xl font-bold text-green-400">
                      {new Intl.NumberFormat('es-AR', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                      }).format(stats.mean)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Volatilidad */}
              <div className="glass-effect rounded-xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Desviación Estándar</p>
                    <p className="text-lg font-bold text-purple-400">
                      {new Intl.NumberFormat('es-AR', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                      }).format(stats.std)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Segunda fila: Gráfico de stock */}
          {stockData && (
            <div>
              <StockChart stockData={stockData} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StatsPanel;